/**
 * IntegrationsSheet — Settings → Integrations hub (Batch 49).
 *
 * Houses connections to external services. First citizen: Google Calendar
 * (two-way read/write sync). Future: My Fitness Pal, Garmin Connect, Apple
 * Health, etc. Each integration is a self-contained card so adding the
 * next one is just a new card component below.
 *
 * Why a dedicated sheet rather than a per-person profile section?
 *   - Integrations are account-level, not person-level. The connecting
 *     user authorises their OWN external account.
 *   - As the list grows, the profile flow would get crowded.
 */
import React, { useEffect, useState } from "react";
import { Calendar, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BottomSheet } from "@/components/BottomSheet";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export function IntegrationsSheet({ open, onClose }) {
  if (!open) return null;
  return (
    <BottomSheet open={open} onClose={onClose} title="Integrations" fullHeight>
      <div className="space-y-4 pb-6" data-testid="integrations-sheet">
        <p className="text-[12px] text-muted-foreground leading-relaxed">
          Connect external services to Kinly. Each integration is opt-in — you control what flows in and out.
        </p>
        <GoogleCalendarCard />
        <ComingSoonCard
          label="My Fitness Pal"
          subtitle="Pull macros + meal logs into Kinly Meal Planner"
        />
        <ComingSoonCard
          label="Garmin Connect"
          subtitle="Activity + sleep data on the family agenda"
        />
        <ComingSoonCard
          label="Apple Health"
          subtitle="Steps, workouts, sleep — read-only"
        />
      </div>
    </BottomSheet>
  );
}

function ComingSoonCard({ label, subtitle }) {
  return (
    <article className="kinly-card flex items-center gap-3 p-4 opacity-60" data-testid={`integration-${label.toLowerCase().replace(/\s+/g, "-")}-soon`}>
      <div className="w-10 h-10 rounded-full bg-muted text-muted-foreground inline-flex items-center justify-center shrink-0">
        <Calendar className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[15px] font-medium leading-tight">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Soon</span>
    </article>
  );
}

const DEFAULT_PUSH = { events: true, activities: true, chores: false, todos: false };

function GoogleCalendarCard() {
  const { family } = useAuth();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [push, setPush] = useState(DEFAULT_PUSH);

  useEffect(() => {
    if (!family?.id) return;
    api.get(`/families/${family.id}/google-calendar/status`)
      .then((r) => {
        setStatus(r.data);
        if (r.data?.push_settings) setPush({ ...DEFAULT_PUSH, ...r.data.push_settings });
      })
      .catch(() => setStatus({ connected: false }));
  }, [family?.id]);

  // Handle OAuth-redirect query params (?gcal=connected | error)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("gcal") === "connected" && family?.id) {
      api.get(`/families/${family.id}/google-calendar/status`)
        .then((r) => {
          setStatus(r.data);
          if (r.data?.push_settings) setPush({ ...DEFAULT_PUSH, ...r.data.push_settings });
        });
      toast.success("Google Calendar connected.");
      const url = new URL(window.location.href);
      url.searchParams.delete("gcal");
      window.history.replaceState({}, "", url.toString());
    } else if (p.get("gcal") === "error") {
      toast.error("Couldn't connect Google Calendar — please try again.");
    }
  }, [family?.id]);

  const onConnect = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(
        `/families/${family.id}/google-calendar/connect/start`,
      );
      window.location.href = data.authorization_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  const onSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/families/${family.id}/google-calendar/sync`);
      toast.success(
        `Synced — ${data.synced_in || data.synced || 0} pulled in` +
        (typeof data.synced_out === "number" ? `, ${data.synced_out} pushed out.` : "."),
      );
      const r = await api.get(`/families/${family.id}/google-calendar/status`);
      setStatus(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onDisconnect = async () => {
    if (!window.confirm("Disconnect Google Calendar? Pulled events will be removed; events pushed to Google stay there.")) return;
    setBusy(true);
    try {
      await api.post(`/families/${family.id}/google-calendar/disconnect`);
      toast.message("Google Calendar disconnected.");
      setStatus({ connected: false });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const onTogglePush = async (key, value) => {
    const next = { ...push, [key]: value };
    setPush(next);
    try {
      await api.patch(`/families/${family.id}/google-calendar/push-settings`, next);
    } catch (e) {
      toast.error("Couldn't save preference.");
      setPush(push); // revert
    }
  };

  if (!status) return null;

  return (
    <article className="kinly-card p-4 space-y-3" data-testid="integration-google-calendar">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-[#4285F4]/10 text-[#4285F4] inline-flex items-center justify-center shrink-0">
          <Calendar className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-display text-[15px] font-medium leading-tight">Google Calendar</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {status.connected
              ? `Read-only sync · ${status.google_email || "your account"}`
              : "Pull your Google events into Kinly so they show on the family agenda"}
          </p>
        </div>
      </div>

      {!status.connected && (
        <button
          data-testid="gcal-connect"
          onClick={onConnect}
          disabled={busy}
          className="w-full h-10 rounded-lg bg-foreground text-background text-sm font-medium tap disabled:opacity-50 inline-flex items-center justify-center gap-2"
        >
          {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Opening Google…</> : "Connect Google Calendar"}
        </button>
      )}

      {status.connected && (
        <>
          {/* Read-only stage 1 — push toggles will land after the
              write-scope verification clears (stage 2). The UI is parked
              behind a soft "coming soon" note so testers see the roadmap. */}
          <div className="rounded-lg border border-dashed border-border p-3 bg-secondary/30">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-1">Push to Google</p>
            <p className="text-[12px] text-muted-foreground leading-snug">
              Pushing Kinly events to Google goes live after Google approves write access. For now we pull your Google events into Kinly.
            </p>
          </div>

          {status.last_sync_at && (
            <p className="text-[11px] text-muted-foreground">
              Last sync {new Date(status.last_sync_at).toLocaleString()}
              {typeof status.last_sync_count === "number" ? ` · ${status.last_sync_count} events in` : ""}
              {typeof status.last_pushed_count === "number" ? ` · ${status.last_pushed_count} out` : ""}
            </p>
          )}
          {status.last_sync_error && (
            <p className="text-[11px] text-rose-600 dark:text-rose-400">
              Last sync error: {status.last_sync_error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              data-testid="gcal-sync"
              onClick={onSync}
              disabled={busy}
              className="flex-1 h-10 rounded-lg border border-border text-sm font-medium tap disabled:opacity-50"
            >
              {busy ? "Syncing…" : "Sync now"}
            </button>
            <button
              data-testid="gcal-disconnect"
              onClick={onDisconnect}
              disabled={busy}
              className="h-10 px-4 rounded-lg border border-border text-sm font-medium text-muted-foreground hover:text-rose-600 tap disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </>
      )}
    </article>
  );
}

function PushToggle({ k, label, hint, value, onChange }) {
  return (
    <label className="flex items-center justify-between gap-3 cursor-pointer py-1" data-testid={`gcal-push-${k}`}>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-medium leading-tight">{label}</p>
        <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">{hint}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={() => onChange(k, !value)}
        data-testid={`gcal-push-${k}-switch`}
        className={[
          "relative w-10 h-6 rounded-full transition-colors shrink-0",
          value ? "bg-primary" : "bg-muted",
        ].join(" ")}
      >
        <span className={[
          "absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform",
          value ? "translate-x-[18px]" : "translate-x-0.5",
        ].join(" ")} />
      </button>
    </label>
  );
}
